import {
  ChevronDown,
  History,
  Info,
  LockKeyhole,
  Plus,
  Trophy,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Pressable } from "react-native";
import { Square, View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { getDayJs, getDayJsFromDate } from "../../../core/dayJs";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { CenterContent } from "../../../ui/CenterContent";
import { ModuleEditor } from "../../../ui/editor/useModuleEditor";
import { log } from "../../../ui/log";
import { TipsText } from "../../../ui/TipsText";
import { Title } from "../../../ui/Title";
import { DateStartDateEndModal } from "../../shared/DateStartDateEndModal";
import type TrackerModuleModel from "../TrackerModuleModel";
import { AddEntryModal } from "./AddEntryModal";
import { TrackerHistoryList } from "./TrackerHistoryList";
import { TrackerLeaderboard } from "./TrackerLeaderboard";
import { TrackerOptionsModal } from "./TrackerOptionsModal";

export const TrackerRules = observer(
  ({ module, isOpen }: { module: TrackerModuleModel; isOpen: boolean }) => {
    const [open, setIsOpen] = React.useState(isOpen);

    return (
      <>
        <Pressable
          onPress={() => {
            setIsOpen(!open);
          }}
        >
          <XStack
            gap="$medium"
            alignItems="center"
            width={"100%"}
            flex={1}
            backgroundColor={"$backgroundBlock"}
            padding="$medium"
          >
            <Info />
            <Body flex={1}>Rules & information</Body>

            <Square animation="quick" rotate={open ? "180deg" : "0deg"}>
              <ChevronDown size="$medium" />
            </Square>
          </XStack>
        </Pressable>

        {open && (
          <Box>
            <ModuleEditor module={module} />
          </Box>
        )}
      </>
    );
  },
);

export const TrackerModuleUI = observer(
  ({ module }: { module: TrackerModuleModel }) => {
    const { modulesDataApi } = useApi();
    const moduleUid = module.uid;
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [showLeaderboard, setShowLeaderboard] = React.useState(true);

    React.useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(moduleUid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${moduleUid}`,
        );
        unsub();
      };
    }, [modulesDataApi, moduleUid]);

    const myUserData = module.myUserData;

    const hasOptions = module.activeOptions.length > 0;

    return (
      <YStack gap="$medium">
        {!module.hasStarted && module.data.settings.dateStart && (
          <CenterContent
            icon={LockKeyhole}
            title={"Not started yet!"}
            subTitle={`This will start ${getDayJs(
              module.data.settings.dateStart,
            )}`}
          />
        )}

        {module.hasEnded && module.realDateEnd && (
          <CenterContent
            icon={LockKeyhole}
            title={"This is finished!"}
            subTitle={`Ended ${getDayJsFromDate(module.realDateEnd)}`}
          />
        )}

        {module.isCurrentlyRunning && module.realDateEnd && (
          <CenterContent
            muted
            icon={Info}
            subTitle={`Ends ${getDayJsFromDate(module.realDateEnd)}`}
          />
        )}

        {module.data.settings.useDescription && (
          <TrackerRules module={module} isOpen={!module.hasStarted} />
        )}

        {module.hasData && (
          <Box>
            <XStack
              gap="$medium"
              alignItems="center"
              justifyContent="space-between"
            >
              {showLeaderboard && (
                <XStack gap="$medium" alignItems="center">
                  <Trophy />
                  <Title>Toplist</Title>
                </XStack>
              )}
              {!showLeaderboard && (
                <XStack gap="$medium" alignItems="center">
                  <History />
                  <Title>Activity</Title>
                </XStack>
              )}

              <Button
                type="ghost"
                onPress={() => {
                  setShowLeaderboard(!showLeaderboard);
                }}
              >
                {showLeaderboard ? "Show Activity" : "Show Toplist"}
              </Button>
            </XStack>

            {showLeaderboard && <TrackerLeaderboard module={module} />}
            {!showLeaderboard && (
              <TrackerHistoryList
                module={module}
                history={module.combinedHistory}
                showEdit={false}
                initialAmount={10}
              />
            )}
          </Box>
        )}

        {!hasOptions && (
          <Box>
            <CenterContent
              icon={Info}
              title="Add options"
              subTitle={
                "You will manage your options from the menu once you have added your first one"
              }
            >
              <View alignSelf="stretch">
                <Button onPress={() => module.setShowOptionsModal(true)}>
                  Manage options
                </Button>
              </View>
            </CenterContent>
          </Box>
        )}

        {hasOptions && module.isCurrentlyRunning && (
          <Box>
            <Title>My Entries ({myUserData?.data.history.length ?? 0})</Title>

            {module.dailyEntryLimitReached && (
              <TipsText text="You have reached the daily limit..." />
            )}

            <Button
              disabled={module.dailyEntryLimitReached}
              icon={Plus}
              onPress={() => setShowAddModal(true)}
            >
              Add Entry
            </Button>

            {!myUserData?.data.history.length && (
              <CenterContent
                icon={Trophy}
                title="Add your first entry"
                subTitle={"Earn your spot in the Toplist, good luck!"}
              />
            )}

            <TrackerHistoryList
              module={module}
              history={myUserData?.data.history}
              showEdit
            />
          </Box>
        )}

        <AddEntryModal
          visible={showAddModal}
          module={module}
          onClose={() => setShowAddModal(false)}
        />

        <TrackerOptionsModal
          visible={module.showOptionsModal}
          module={module}
          onClose={() => module.setShowOptionsModal(false)}
        />

        {module.showDatesFormSheet && (
          <DateStartDateEndModal
            visible
            module={module}
            initialDateStart={module.data.settings.dateStart}
            initialDateEnd={module.data.settings.dateEnd}
            onClose={() => module.toggleDatesFormSheet()}
            onSave={(dates) => {
              void module.saveSettings(dates);
            }}
          />
        )}
      </YStack>
    );
  },
);
